import * as React from 'react';

import { FormProvider, useForm } from 'react-hook-form';

import {
  Button,
  Grid,
  Paper,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';

import { DateTime } from 'luxon';
import { getColor } from '../core/colors';
import { AirportInfo, EventReport, FlightLookup } from '../core/api/clients';

import { useAirportInfo } from '../core/context/AirportInfoContext';
import useIsMobile from '../core/hooks/useIsMobile';

import ControlledForm from './ControlledForm';

import ControlledTextField from './ControlledTextField';
import ControlledDatePicker from './ControlledDatePicker';
import ControlledAirportSelect from './ControlledAirportSelect';
import FormPaper from './FormPaper';
import { FormValidationProvider } from '../core/context/FormValidationContext';
import ReportLinkContainer from './ReportLinkContainer';

interface FlightLookupFormProps {
  onSubmit: (flightLookup: FlightLookup) => void;
  onSubmitFromExisting?: (eventReport: EventReport) => void;
  onCancel: () => void;
  onBlank: () => void;
  color: string | null | undefined;
  loading: boolean;
  isDateDefault?: boolean;
  reports?: EventReport[];
}

function FlightLookupForm({
  onSubmit,
  onCancel,
  onBlank,
  color,
  loading,
  isDateDefault = false,
  reports,
  onSubmitFromExisting = () => {},
}: FlightLookupFormProps) {
  const isMobile: boolean = useIsMobile();
  const airportInfo: AirportInfo[] | undefined = useAirportInfo();

  const { control, handleSubmit, ...formProps } = useForm<FlightLookup>();

  const handleCreateBlankButtonClick = () => onBlank();
  React.useEffect(() => {
    if (isDateDefault) {
      formProps.setValue('flightDate', DateTime.now().toISO());
    }
  }, []);

  return (
    <React.Fragment>
      <FormPaper color={color}>
        <ControlledForm onSubmit={handleSubmit(onSubmit)}>
          <FormProvider control={control} handleSubmit={handleSubmit} {...formProps}>
            <FormValidationProvider<FlightLookup>>
              <Typography variant="h5" sx={{ mb: 1 }}>Create Form</Typography>
              <Typography
                variant="body1"
                sx={{
                  mb: 0,
                  color: getColor(color)[500],
                  fontWeight: 300,
                }}
              >
                In order to find the correct data for the form, please enter the information below
              </Typography>
              <Paper variant="outlined" sx={{ my: 2, p: isMobile ? 1 : 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <ControlledTextField<FlightLookup>
                      name="flightNumber"
                      control={control}
                      rules={{ required: true, maxLength: 4, pattern: /[A-Z0-9]{3,}/ }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <ControlledDatePicker<FlightLookup>
                      name="flightDate"
                      control={control}
                      rules={{ required: true }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <ControlledAirportSelect<FlightLookup>
                      name="departureStation"
                      label="Scheduled Departure Airport"
                      control={control}
                      options={airportInfo ?? []}
                      rules={{ required: true }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <ControlledAirportSelect<FlightLookup>
                      name="arrivalStation"
                      label="Scheduled Arrival Airport"
                      control={control}
                      options={airportInfo ?? []}
                      rules={{ required: true }}
                    />
                  </Grid>
                </Grid>
              </Paper>
              <Grid container spacing={2} justifyContent="end">
                <Grid item>
                  <LoadingButton
                    type="submit"
                    variant="contained"
                    loading={loading}
                  >
                    Submit
                  </LoadingButton>
                </Grid>
              </Grid>
            </FormValidationProvider>
          </FormProvider>
        </ControlledForm>
      </FormPaper>
      {reports
      && (
      <ReportLinkContainer
        color={color}
        reports={reports?.map((report) => report as EventReport)}
        headerText="Selecting a form will create a new DER with the same information as the form selected below."
        subText="Create From Existing"
        onPrimaryClick={onSubmitFromExisting}
        primaryText="Submit"
      />
      )}
      <FormPaper color={color}>
        <Grid container justifyContent="space-between" alignItems="center">
          <Grid item flexGrow={1}>
            <Typography variant="body1">Please utilize the Create Blank Form option only if your event</Typography>
            <Typography variant="body2" sx={{ ml: 2 }}>Had no flight number associated with it</Typography>
            <Typography variant="body2" sx={{ ml: 2 }}>Involved only one crew member</Typography>
            <Typography variant="body2" sx={{ ml: 2 }}>Occurred on a canceled segment where the flight did not operate</Typography>
          </Grid>
          <Grid item>
            <Button onClick={handleCreateBlankButtonClick} variant="outlined">Create Blank Form</Button>
          </Grid>
        </Grid>
      </FormPaper>
      <Grid container justifyContent="end">
        <Grid item>
          <Button onClick={onCancel}>Cancel</Button>
        </Grid>
      </Grid>
    </React.Fragment>
  );
}

export default FlightLookupForm;
